/**
 * Meta / WhatsApp Cloud API + server errors → Hebrew (user-facing).
 * מקור מידע יחיד לסטטוסי הודעות יוצאות, שגיאות Meta וטקסטי הנחיה.
 * @see https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes
 */
import axios from 'axios';

const WHATSAPP_FRIENDLY_MESSAGES_CORE: Record<string, string> = {
  '130429':
    'חריגה מקצב השליחה המותר. המערכת תמתין רגע לפני ניסיון נוסף.',
  '131026':
    'הנמען אינו תקין או שאינו רשום בוואטסאפ. בדקו את מספר הטלפון.',
  '131047':
    'ניסית לשלוח הודעה רגילה כשאין חלון שיחה פתוח מול הנמען. ניתן לשלוח תבנית בלבד.',
  '131049':
    'השליחה נחסמה עקב חריגה ממדיניות פייסבוק לשמירה על חוויית משתמש.',
  '131051':
    'הנמען אינו תקין או שאינו רשום בוואטסאפ. בדקו את מספר הטלפון.',
  '500':
    'שגיאת שרת פנימית בשמירת ההודעה. המידע הועבר לטיפול טכני.',
  '131031': 'תבנית ההודעה אינה זמינה או שאינה מאושרת.',
  '132000': 'פרמטרים לא תקינים בבקשת השליחה.',
  '132001': 'שגיאת מערכת בצד וואטסאפ — נסו שוב מאוחר יותר.',
  '133010': 'חשבון העסק אינו מחובר, חסום או שאינו מאומת — בדקו ב-Business Manager.',
  '131042':
    'חסר אמצעי תשלום תקין בפייסבוק — יש לעדכן כרטיס אשראי ב-Business Manager של מטא.',
  '131048':
    'מטא חסמה שליחה מהמספר — חריגה ממכסת ההודעות היומית או הגבלת איכות.',
  '131056':
    'נשלחו יותר מדי הודעות לאותו נמען בפרק זמן קצר — יש להמתין או לפזר שליחות.',
  '130472':
    'ההודעה לא נשלחה במסגרת ניסוי של וואטסאפ (מדגם משתמשים).',
  '131052': 'לא ניתן להוריד את קובץ המדיה מההודעה.',
  '131053': 'לא ניתן להעלות את המדיה לשליחה (פורמט/גודל/סוג לא נתמך).',
  '133004': 'השרת של מטא זמנית לא זמין — נסו שוב בעוד רגע.',
  '131016': 'שירות זמני לא זמין בצד מטא — נסו שוב מאוחר יותר.',
  '131057': 'חשבון העסק במצב תחזוקה (למשל שדרוג מסירה) — נסו שוב מאוחר יותר.',
  '131000': 'שגיאה לא ידועה בצד וואטסאפ — נסו שוב או פנו לתמיכה.',
  '131005': 'הגישה נדחתה — בדקו טוקן והרשאות לאפליקציה.',
  '131037': 'שם התצוגה של מספר העסק טרם אושר — יש להשלים אישור ב-Business Manager.',
  '131050': 'הנמען ביטל קבלת הודעות שיווק ממספר העסק.',
  '132015': 'התבנית מושהית בגלל איכות נמוכה — לא ניתן לשלוח אותה כרגע.',
  '132016': 'התבנית הושבתה לצמיתות בגלל איכות נמוכה — יש ליצור תבנית חדשה.',
  '132007': 'תוכן התבנית מפר מדיניות וואטסאפ.',
};

/**
 * נוסח שגיאה באנגלית כפי שמופיע ב-WhatsApp Cloud API / webhook של מטא.
 * משמש להצגה בדיאלוג כשאין מחרוזת גולמית שמורה בשדה Status.
 */
const WHATSAPP_META_RAW_ENGLISH: Record<string, string> = {
  '130429': '(#130429) Rate limit hit',
  '131026': '(#131026) Message undeliverable',
  '131047': '(#131047) Re-engagement message',
  '131049':
    '(#131049) This message was not delivered to maintain healthy ecosystem engagement.',
  '131051': '(#131051) Message undeliverable',
  '500': 'Internal Server Error',
  '131031': '(#131031) Template name does not exist in the translation',
  '132000': '(#132000) Parameter value is not valid',
  '132001': '(#132001) Service unavailable',
  '133010': '(#133010) Account not registered',
  '131042': '(#131042) Business eligibility payment issue',
  '131048': '(#131048) Message failed to send because of spam rate limits or quality restrictions',
  '131056': '(#131056) Too many messages sent to the same recipient in a short period',
  '130472': '(#130472) User\'s number is part of an experiment',
  '131052': '(#131052) Unable to download media',
  '131053': '(#131053) Unable to upload media',
  '133004': '(#133004) Server unavailable',
  '131016': '(#131016) Service unavailable',
  '131057': '(#131057) Account in maintenance mode',
  '131000': '(#131000) Unknown error',
  '131005': '(#131005) Access denied',
  '131037': '(#131037) Display name not approved',
  '131050': '(#131050) User opted out of marketing messages',
  '132015': '(#132015) Template paused due to low quality',
  '132016': '(#132016) Template permanently disabled due to low quality',
  '132007': '(#132007) Template content violates policy',
};

/**
 * נוסחים נוספים שמטא שולחת בפועל לאותם קודים. המילון למעלה מחזיק נוסח קנוני
 * אחד לכל קוד, אבל ה-webhook מחזיר לעיתים ניסוח קצר ושונה לגמרי — למשל
 * "Spam Rate limit hit" לקוד 131048. בלי הרשימה הזאת הטקסט לא נקשר לשום קוד,
 * והמשתמש ראה מחרוזת אנגלית גולמית בלי הסבר ובלי הנחיית טיפול.
 * (מ-2026-10-29 השרת שומר גם את הקוד עצמו; זה נשאר בשביל נתונים היסטוריים.)
 */
const WHATSAPP_META_RAW_ENGLISH_ALIASES: Record<string, string[]> = {
  '131048': ['spam rate limit hit', 'message failed to send because of spam rate limits'],
  '130429': ['rate limit hit', 'rate limit issues'],
  '131026': ['message undeliverable', 'receiver is incapable of receiving this message'],
  '131050': ['user opted out', 'recipient has opted out'],
  '132015': ['template is paused'],
  '132016': ['template is disabled'],
};

const KNOWN_INTERNAL_STATUS_VALUES = new Set([
  'sent',
  'delivered',
  'read',
  'error',
  'send_failed',
  'sending',
  'uploading',
  'pending whatsapp',
  'pending system',
  'not sent',
  'validated_ready_to_send',
  'failed',
]);

/** נוסח מדיניות + הדגשה לדיאלוג «תבנית שנחסמה» */
export const TEMPLATE_SPAM_BLOCK_DIALOG = {
  intro: 'שליחת התבנית נחסמה ע"י מנגון סינון ספאם של מטא (פייסבוק).',
  quality:
    'בדקו את איכות התבנית. אם איכות התבנית ירודה או נשחקה, ניתן לנסות ליצור ולשלוח תבנית חדשה.',
  waitPrefix: 'המתינו לפחות 24 שעות לפני ניסיון נרחב, ועיינו ב',
  waitLinkLabel: 'מדיניות האכיפה של מטא',
  utilityEmphasis:
    'בכל מקרה, ניתן לפתוח שיחה כרגיל באמצעות שליחת תבנית מקטגוריית שירות (utility)',
} as const;

export type OutgoingErrorDialogVariant = 'default' | 'template_spam_block';

/** סטטוסים טכניים מהשרת (snake_case) — לא קודי Meta, אך מוצגים למשתמש בעברית. */
export const INTERNAL_PIPELINE_STATUS_STRINGS: Record<string, string> = {
  validated_ready_to_send: 'ההודעה מאושרת וממתינה לשליחה סופית',
};

/** סטטוסי מסירה / שליחה (אנגלית מהשרת או מהלקוח) → עברית */
export const OUTGOING_MESSAGE_STATUS_HEB: Record<string, string> = {
  sent: 'נשלחה',
  delivered: 'נמסרה',
  read: 'נקראה',
  sending: 'נשלחת…',
  uploading: 'מעלה קובץ — ההודעה תשלח בסיום ההעלאה',
  'pending whatsapp': 'בתהליך שליחה לוואטסאפ',
  'pending system': 'בתהליך שליחה במערכת',
  send_failed: 'השליחה נכשלה — לחץ על נסה שוב',
  error: 'שגיאה בשליחה',
};

/** סטטוסים תקינים / בתהליך — לא מציגים אזור שגיאה אדום (כולל send_failed) */
export const OUTGOING_PIPELINE_OK_STATUSES = [
  'sent',
  'delivered',
  'read',
  'pending whatsapp',
  'pending system',
  'sending',
  'uploading',
  'validated_ready_to_send',
  'send_failed',
] as const;

/** סטטוסים שלא נספרים כשליחה שנכשלה ברשימת שיחות / סינון */
export const OUTGOING_NON_FAILURE_STATUSES = [
  'sent',
  'delivered',
  'read',
  'pending whatsapp',
  'pending system',
  'sending',
  'uploading',
  'validated_ready_to_send',
] as const;

const OUTGOING_PIPELINE_OK_SET = new Set<string>(OUTGOING_PIPELINE_OK_STATUSES);
const OUTGOING_LIST_OK_SET = new Set<string>(OUTGOING_NON_FAILURE_STATUSES);

/** שגיאת חיבור מהשרת שלנו ל-graph.facebook.com (מטא) — לא שגיאת Meta API רגילה */
export const META_GRAPH_CONNECTION_ERROR_DISPLAY =
  'לא הצלחנו להתחבר לשרתי וואטסאפ (מטא). ההודעה לא נשלחה.';

export const META_GRAPH_CONNECTION_ERROR_PREVENTION =
  'זו תקלת חיבור זמנית בין המערכת לשרתי מטא (WhatsApp) — לא בעיה בתוכן ההודעה, בתבנית או במספר הנמען.\n\n' +
  'מה לעשות:\n' +
  '• המתינו 2–5 דקות ונסו לשלוח שוב.\n' +
  '• ודאו שיש חיבור אינטרנט יציב ברשת או במשרד.\n' +
  '• אם השגיאה חוזרת — ייתכן תקלה זמנית אצל מטא; בדקו את סטטוס השירותים (metastatus.com).\n' +
  '• אם נמשכת מעל שעה — פנו לתמיכה עם שעת השליחה. אין צורך להעתיק את הטקסט הטכני.';

/** מילה להדגשה בשורת שגיאת תשלום — כדי שלא יתבלבל עם תשלום לפאנר */
export const META_PAYMENT_DISPLAY_EMPHASIS = 'בפייסבוק';

export function isMetaGraphConnectionError(raw: string | null | undefined): boolean {
  if (raw == null || !String(raw).trim()) return false;
  const lower = String(raw).toLowerCase();
  if (lower.includes('graph.facebook.com')) return true;
  if (lower.includes('connection attempt failed')) return true;
  if (lower.includes('connected party did not properly respond')) return true;
  if (lower.includes('connected host has failed to respond')) return true;
  if (lower.includes('facebook.com:443')) return true;
  if (
    (lower.includes('timeout') || lower.includes('timed out') || lower.includes('time-out')) &&
    (lower.includes('facebook') || lower.includes('whatsapp') || lower.includes('meta'))
  ) {
    return true;
  }
  return false;
}

/** מחרוזות שגיאה חופשיות (אנגלית מ-Meta / webhook / שרת) → עברית קצרה */
const ENGLISH_STATUS_ERROR_PHRASES: Record<string, string> = {
  'not sent': 'ההודעה לא נשלחה',
  failed: 'שליחת ההודעה נכשלה',
  rejected: 'ההודעה נדחתה',
  expired: 'תוקף ההודעה פג',
  blocked: 'הנמען חסם את המספר',
  spam: 'ההודעה זוהתה כספאם',
  'message failed to send': 'שליחת ההודעה נכשלה',
  'message undelivered': 'ההודעה לא נמסרה',
  'message undeliverable': 'ההודעה לא ניתנת למסירה',
  're-engagement message':
    'ניסית לשלוח הודעה רגילה כשאין חלון שיחה פתוח מול הנמען. ניתן לשלוח תבנית בלבד.',
  're-engagement':
    'ניסית לשלוח הודעה רגילה כשאין חלון שיחה פתוח מול הנמען. ניתן לשלוח תבנית בלבד.',
  'message not delivered': 'ההודעה לא נמסרה',
  'delivery failed': 'המסירה נכשלה',
  'template does not exist': 'התבנית אינה קיימת',
  'template not found': 'התבנית לא נמצאה',
  'template paused': 'התבנית מושהית זמנית',
  'template rejected': 'התבנית נדחתה על ידי וואטסאפ',
  'template expired': 'תוקף התבנית פג',
  'template disabled': 'התבנית הושבתה',
  'invalid recipient': 'מספר טלפון לא תקין',
  'recipient not in contacts': 'הנמען לא ברשימת אנשי הקשר',
  'recipient phone number invalid': 'מספר טלפון לא תקין',
  'phone number not registered': 'מספר טלפון לא רשום בוואטסאפ',
  'user not found': 'המשתמש לא נמצא',
  'recipient not opted in': 'הנמען לא אישר קבלת הודעות',
  'rate limit exceeded': 'חרגת ממגבלת ההודעות היומית',
  'quota exceeded': 'חרגת ממגבלת ההודעות החודשית',
  'too many requests': 'יותר מדי בקשות, נסה שוב מאוחר יותר',
  'rate limit hit': 'חרגת ממגבלת ההודעות',
  'daily message limit exceeded': 'חרגת ממגבלת ההודעות היומית',
  'monthly message limit exceeded': 'חרגת ממגבלת ההודעות החודשית',
  'business eligibility payment issue':
    'חסר אמצעי תשלום תקין בפייסבוק — יש לעדכן כרטיס אשראי ב-Business Manager של מטא',
  'insufficient account balance': 'יתרת חשבון לא מספקת בפייסבוק — יש לעדכן אמצעי תשלום במטא',
  'payment required': 'נדרש לעדכן אמצעי תשלום בפייסבוק ב-Business Manager של מטא',
  'account suspended': 'החשבון הושעה זמנית — בדקו ב-Business Manager',
  'account restricted': 'החשבון מוגבל — בדקו סטטוס ב-Business Manager',
  'account blocked': 'החשבון חסום — יש לבדוק ולפתור חסימה ב-Business Manager',
  'business verification required': 'נדרש אימות עסקי',
  'media download failed': 'הורדת המדיה נכשלה',
  'media upload failed': 'העלאת המדיה נכשלה',
  'media too large': 'הקובץ גדול מדי',
  'unsupported media type': 'סוג קובץ לא נתמך',
  'media not found': 'המדיה לא נמצאה',
  'image too large': 'התמונה גדולה מדי',
  'video too large': 'הסרטון גדול מדי',
  'file too large': 'הקובץ גדול מדי',
  'invalid parameter': 'פרמטרים לא תקינים',
  'parameter format mismatch': 'פורמט הפרמטרים שגוי',
  'missing required parameter': 'חסרים פרמטרים נדרשים',
  'parameter value invalid': 'ערך פרמטר לא תקין',
  'network error': 'בעיית רשת, נסה שוב',
  'connection attempt failed': META_GRAPH_CONNECTION_ERROR_DISPLAY,
  'connected party did not properly respond': META_GRAPH_CONNECTION_ERROR_DISPLAY,
  'connected host has failed to respond': META_GRAPH_CONNECTION_ERROR_DISPLAY,
  'graph.facebook.com': META_GRAPH_CONNECTION_ERROR_DISPLAY,
  timeout: 'הבקשה פגה, נסה שוב',
  'service unavailable': 'השירות לא זמין כרגע',
  'internal server error': 'שגיאת שרת, נסה שוב מאוחר יותר',
  'bad request': 'בקשה שגויה',
  'message too old': 'ההודעה ישנה מדי',
  'message already sent': 'ההודעה כבר נשלחה',
  'conversation not found': 'השיחה לא נמצאה',
  'conversation expired': 'תוקף השיחה פג',
  'whatsapp account not connected': 'חשבון וואטסאפ לא מחובר',
  'whatsapp api error': 'שגיאת וואטסאפ, נסה שוב',
  'whatsapp business account issue': 'בעיה בחשבון העסקי',
  'phone number quality': 'מספר טלפון לא איכותי',
  unsubscribed: 'הנמען ביטל הרשמה',
  'authentication failed': 'אימות נכשל',
  'invalid access token': 'טוקן גישה לא תקין',
  'token expired': 'טוקן הגישה פג תוקף',
  unauthorized: 'אין הרשאה לשלוח',
  forbidden: 'הגישה נדחתה',
  'unknown error': 'שגיאה לא ידועה',
  'error occurred': 'אירעה שגיאה',
  'operation failed': 'הפעולה נכשלה',
  'request failed': 'הבקשה נכשלה',
  'send failed': 'שליחה נכשלה',
  'healthy ecosystem engagement':
    'השליחה נחסמה עקב חריגה ממדיניות פייסבוק לשמירה על חוויית משתמש',
  'this message was not delivered to maintain healthy ecosystem engagement':
    'השליחה נחסמה עקב חריגה ממדיניות פייסבוק לשמירה על חוויית משתמש',
  'policy enforcement': 'השליחה נדחתה — הפרת מדיניות וואטסאפ',
};

/** פירוט חלון 24 שעות / Re-engagement — קוד 131047 */
export const MESSAGE_RE_ENGAGEMENT_PREVENTION =
  'ניסית לשלוח הודעה רגילה כשאין חלון שיחה פתוח מול הנמען.\n' +
  '• לאחר 24 שעות מהודעת הלקוח האחרונה, חלון השיחה נסגר.\n' +
  '• בשלב זה ניתן לשלוח רק הודעות תבנית (Template) שאושרו מראש.\n\n' +
  'בחרו תבנית מהמערכת או פנו למנהל התבניות.';

/** פירוט «Message undeliverable» — קודים 131026 / 131051 ומחרוזות דומות */
export const MESSAGE_UNDELIVERABLE_PREVENTION =
  'ההודעה לא ניתנת למסירה בשל אחת משלוש סיבות אפשריות:\n' +
  '• הגדרות הפרטיות של הנמען אינן מאפשרות קבלת הודעות עסקיות.\n' +
  '• הנמען חסם אפשרות קבלת הודעות ממספר זה.\n' +
  '• מספר הטלפון לא רשום בוואטסאפ.\n\n' +
  'מטא (פייסבוק) לא מספקת את הסיבה הספציפית מטעמי הגנה על פרטיות.';

/** הנחיות «מניעה וטיפול» למחרוזות שגיאה חופשיות (אנגלית) */
const ENGLISH_STATUS_ERROR_PREVENTION: Record<string, string> = {
  'not sent': 'בדקו חיבור, תוקף חלון 24 שעות, וסטטוס מספר היעד. אם מדובר בשיווק — שלחו תבנית מאושרת.',
  failed: 'נסו שוב בעוד מספר דקות. אם חוזר — בדקו את סטטוס החשבון ב-Business Manager.',
  rejected: 'בדקו שהתוכן עומד במדיניות וואטסאפ ושהתבנית פעילה ומאושרת.',
  blocked: 'הלקוח חסם את המספר או לא מקבל הודעות. אל תנסו שליחה חוזרת מיידית.',
  spam: 'הפחיתו תדירות שליחה, הימנעו מתוכן שיווקי אגרסיבי, ובדקו איכות מספר ב-WhatsApp Manager.',
  'message failed to send': 'בדקו מספר יעד, חיבור אינטרנט וסטטוס חשבון וואטסאפ ביזנס.',
  'message undelivered': 'ודאו שהמספר פעיל בוואטסאפ ושלא חלף חלון 24 שעות ללא תבנית.',
  'message undeliverable': MESSAGE_UNDELIVERABLE_PREVENTION,
  're-engagement message': MESSAGE_RE_ENGAGEMENT_PREVENTION,
  're-engagement': MESSAGE_RE_ENGAGEMENT_PREVENTION,
  'message not delivered': 'ודאו שהמספר פעיל בוואטסאפ ושלא חלף חלון 24 שעות ללא תבנית.',
  'delivery failed': 'בדקו מספר יעד, הרשאות חשבון וסטטוס תבנית אם נשלחה מחוץ לחלון 24 שעות.',
  'template does not exist': 'בחרו תבנית פעילה מהמערכת או צרו תבנית חדשה ב-Business Manager.',
  'template not found': 'רעננו רשימת תבניות ובדקו שהתבנית לא נמחקה או הושבתה.',
  'template paused': 'ערכו את התבנית לפי המלצות איכות מטא והמתינו להסרת השהיה.',
  'template rejected': 'עדכנו את ניסוח התבנית והגישו מחדש לאישור מטא.',
  'template expired': 'צרו תבנית חדשה או חדשו תוקף ב-Business Manager.',
  'template disabled': 'הפעילו תבנית חלופית מאושרת או צרו תבנית חדשה.',
  'invalid recipient': 'בדקו פורמט בינלאומי (קידומת מדינה) ועדכנו את מספר הלקוח.',
  'phone number not registered': 'ודאו שהלקוח התקין וואטסאפ במספר זה.',
  'rate limit exceeded':
    'המתינו לפני שליחה נוספת, פזרו קמפיינים, והפחיתו נפח הודעות בפרק זמן קצר.',
  'quota exceeded': 'בדקו מכסת הודעות בחשבון וב-Business Manager.',
  'too many requests': 'המתינו מספר דקות לפני ניסיון נוסף.',
  'business eligibility payment issue':
    'שימו לב: מדובר באמצעי תשלום בחשבון פייסבוק (מטא) — לא בתשלום לפאנר.\n\nבמנהל העסק (Meta) → Billing: הוסיפו או החליפו כרטיס אשראי פעיל, ודאו שאין חסימה או פקיעת תוקף. שמרו ונסו שליחה מחדש.',
  'insufficient account balance':
    'שימו לב: מדובר בחיוב בחשבון פייסבוק (מטא) — לא בתשלום לפאנר.\n\nבמנהל העסק (Meta) → Billing: עדכנו אמצעי תשלום או טענו יתרה, וודאו שהכרטיס מאושר לחיוב בינלאומי.',
  'payment required':
    'שימו לב: מדובר באמצעי תשלום בחשבון פייסבוק (מטא) — לא בתשלום לפאנר.\n\nבמנהל העסק (Meta) → Billing: הוסיפו כרטיס אשראי חדש או החליפו כרטיס שפג תוקפו/נחסם. בדקו גם פרטי חיוב ומס.',
  'account suspended':
    'בדקו התראות ב-Business Manager, פתרו הפרות מדיניות, ופנו לתמיכת מטא אם נדרש.',
  'account restricted': 'בדקו הגבלות חשבון ב-Business Manager והשלימו אימות עסקי.',
  'account blocked':
    'ב-Business Manager בדקו סטטוס WABA וחשבון העסק, פתרו חסימות מדיניות או חיוב, והמתינו לאישור מטא לפני שליחה חוזרת.',
  'business verification required': 'השלימו אימות עסקי ב-Business Manager.',
  'media upload failed': 'בדקו פורמט, גודל וסוג קובץ לפי דרישות וואטסאפ והעלו שוב.',
  'media too large': 'דחסו את הקובץ או שלחו בגודל קטן יותר.',
  'unsupported media type': 'המירו לפורמט נתמך (JPEG, PNG, MP4, PDF וכו׳).',
  'network error': 'בדקו חיבור אינטרנט ונסו שוב.',
  'connection attempt failed': META_GRAPH_CONNECTION_ERROR_PREVENTION,
  'connected party did not properly respond': META_GRAPH_CONNECTION_ERROR_PREVENTION,
  'connected host has failed to respond': META_GRAPH_CONNECTION_ERROR_PREVENTION,
  'graph.facebook.com': META_GRAPH_CONNECTION_ERROR_PREVENTION,
  timeout: 'נסו שוב בעוד רגע — ייתכן עומס זמני בשרת.',
  'service unavailable': 'בדקו סטטוס שירותי מטא (metastatus) ונסו שוב מאוחר יותר.',
  'internal server error': 'נסו שוב בעוד מספר דקות. אם חוזר — פנו לתמיכה עם צילום מסך.',
  'whatsapp account not connected': 'חברו מחדש את חשבון וואטסאפ ביזנס בהגדרות החברה.',
  'phone number quality': 'בדקו ציון איכות ב-WhatsApp Manager והפחיתו תדירות שליחה.',
  unsubscribed: 'אל תשלחו שיווק למספר זה — הלקוח ביטל הסכמה.',
  'invalid access token': 'רעננו טוקן גישה ובדקו הרשאות אפליקציה.',
  'token expired': 'רעננו טוקן גישה ב-Business Manager.',
  unauthorized: 'בדקו הרשאות משתמש וטוקן אפליקציה.',
  forbidden: 'בדקו הרשאות WABA וטוקן גישה.',
  'healthy ecosystem engagement':
    'הפסיקו שליחות חוזרות לאותו לקוח והפחיתו תדירות. בדקו איכות תבניות ב-WhatsApp Manager ובמערכת (תבניות מושהות/נדחות), והמתינו לפחות 24 שעות לפני ניסיון נרחב.',
  'this message was not delivered to maintain healthy ecosystem engagement':
    'הפסיקו שליחות חוזרות לאותו לקוח והפחיתו תדירות. בדקו איכות תבניות ב-WhatsApp Manager ובמערכת (תבניות מושהות/נדחות), והמתינו לפחות 24 שעות לפני ניסיון נרחב.',
  'policy enforcement':
    'עיינו במדיניות וואטסאפ, בדקו איכות ותוכן התבנית, תקנו ניסוח והמתינו לפני שליחה חוזרת.',
};

export const META_POLICY_ENFORCEMENT_URL =
  'https://developers.facebook.com/docs/whatsapp/overview/policy-enforcement/';

export const META_BILLING_SETTINGS_URL = 'https://business.facebook.com/settings/billing';

export const META_TEMPLATE_QUALITY_URL =
  'https://business.facebook.com/wa/manage/message-templates/';

/** מרכז המידע — טאב סטטוסים ושגיאות (נפתח בטאב חדש מדיאלוג «מניעה וטיפול») */
export const INFO_CENTER_STATUSES_ERRORS_URL = '/info-center?tab=statuses-errors';

/** תבניות — נפתח בטאב חדש מפופאפ שגיאה (המשתמש נשאר בשיחה) */
export const APP_TEMPLATES_HOME_URL = '/templates/list';

/** פתיחת קישור מפופאפ שגיאה — תמיד בטאב חדש */
export function openMessageErrorLink(url: string): void {
  if (typeof window === 'undefined') return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** נוסח אחיד לשגיאת healthy ecosystem (מחרוזת גולמית מ-Meta) */
export const META_ECOSYSTEM_DISPLAY_LINE =
  'השליחה נחסמה עקב חריגה ממדיניות פייסבוק לשמירה על חוויית משתמש';

const META_ECOSYSTEM_PREVENTION =
  'הפסיקו שליחות חוזרות לאותו לקוח והפחיתו תדירות קמפיינים. בדקו איכות תבניות ב-WhatsApp Manager ובמערכת — תבניות מושהות, נדחות או באיכות נמוכה עלולות לגרום לחסימה. המתינו לפחות 24 שעות לפני ניסיון נרחב, ועיינו במדיניות האכיפה של מטא.';

export const GENERIC_OUTGOING_ERROR_PREVENTION =
  'אם הבעיה חוזרת, צלמו מסך של ההודעה ופנו לתמיכה עם שעת השליחה ומספר הלקוח.';

/** כשאין מיפוי ספציפי — שורת סיבה קצרה מתחת ל«ההודעה לא נשלחה» */
export const OUTGOING_ERROR_FALLBACK_REASON =
  'לא הצלחנו לשלוח את ההודעה. לפרטים נוספים לחצו «למידע נוסף».';

export function isMessageUndeliverableError(codeOrText: string | number | null | undefined): boolean {
  if (codeOrText == null) return false;
  const str = String(codeOrText).trim();
  if (!str) return false;
  const key = normalizeCodeKey(str);
  if (key === '131026' || key === '131051') return true;
  return str.toLowerCase().includes('undeliverable');
}

export function isReEngagementError(codeOrText: string | number | null | undefined): boolean {
  if (codeOrText == null) return false;
  const str = String(codeOrText).trim();
  if (!str) return false;
  const key = normalizeCodeKey(str);
  if (key === '131047') return true;
  return str.toLowerCase().includes('re-engagement');
}

const ENGLISH_PHRASE_KEYS_SORTED: string[] = [];

function refreshEnglishPhraseKeyIndex(): void {
  ENGLISH_PHRASE_KEYS_SORTED.length = 0;
  ENGLISH_PHRASE_KEYS_SORTED.push(
    ...Object.keys(ENGLISH_STATUS_ERROR_PHRASES).sort((a, b) => b.length - a.length),
  );
}

refreshEnglishPhraseKeyIndex();

function hasHebrew(text: string): boolean {
  return /[\u0590-\u05FF]/.test(text);
}

function resolveEnglishPhrase(raw: string): string | null {
  const statusLower = raw.trim().toLowerCase();
  if (!statusLower) return null;
  if (ENGLISH_STATUS_ERROR_PHRASES[statusLower]) {
    return ENGLISH_STATUS_ERROR_PHRASES[statusLower];
  }
  for (const key of ENGLISH_PHRASE_KEYS_SORTED) {
    if (statusLower.includes(key)) {
      return ENGLISH_STATUS_ERROR_PHRASES[key];
    }
  }
  return null;
}

function resolveEnglishPhrasePrevention(raw: string): string | null {
  const statusLower = raw.trim().toLowerCase();
  if (!statusLower) return null;
  if (ENGLISH_STATUS_ERROR_PREVENTION[statusLower]) {
    return ENGLISH_STATUS_ERROR_PREVENTION[statusLower];
  }
  for (const key of ENGLISH_PHRASE_KEYS_SORTED) {
    if (statusLower.includes(key) && ENGLISH_STATUS_ERROR_PREVENTION[key]) {
      return ENGLISH_STATUS_ERROR_PREVENTION[key];
    }
  }
  return null;
}

function mergedStatusLookup(raw: string): string | null {
  const k = raw.trim().toLowerCase();
  if (WHATSAPP_FRIENDLY_MESSAGES_CORE[k]) return WHATSAPP_FRIENDLY_MESSAGES_CORE[k];
  if (INTERNAL_PIPELINE_STATUS_STRINGS[k]) return INTERNAL_PIPELINE_STATUS_STRINGS[k];
  if (OUTGOING_MESSAGE_STATUS_HEB[k]) return OUTGOING_MESSAGE_STATUS_HEB[k];
  return resolveEnglishPhrase(raw);
}

/** האם המחרוזת נראית כמו סטטוס pipeline ב־snake_case */
export function isSnakeCasePipelineStatus(raw: string | null | undefined): boolean {
  if (raw == null || raw === '') return false;
  const s = String(raw).trim().toLowerCase();
  return /^[a-z][a-z0-9_]*$/.test(s) && s.includes('_');
}

/** האם הסטטוס הוא חסימת Meta «healthy ecosystem» */
export function isMetaHealthyEcosystemBlock(status?: string | null): boolean {
  const s = status?.toLowerCase().trim() ?? '';
  return (
    s.includes('this message was not delivered to maintain healthy ecosystem engagement') ||
    s.includes('healthy ecosystem engagement')
  );
}

/** האם סטטוס יוצא הוא שגיאת מסירה (לא כולל send_failed שמטופל בנפרד) */
export function isOutgoingDeliveryErrorStatus(status?: string | null): boolean {
  if (!status) return false;
  const s = status.toLowerCase().trim();
  if (s === 'send_failed') return false;
  if (s === 'error') return true;
  if (isMetaHealthyEcosystemBlock(s)) return true;
  return !OUTGOING_PIPELINE_OK_SET.has(s);
}

export function isOutgoingLastMessageFailure(status?: string | null): boolean {
  if (!status) return false;
  return !OUTGOING_LIST_OK_SET.has(status.toLowerCase().trim());
}

/** לשמירה ב־StatusHeb כשטוענים היסטוריה / socket */
export function resolveStatusHebFromServerStatus(raw: string | null | undefined): string | null {
  const mapped = mergedStatusLookup(String(raw ?? ''));
  if (mapped) return mapped;
  const friendlyFromMeta = getFriendlyMessageForMetaRaw(raw);
  if (friendlyFromMeta) return friendlyFromMeta;
  if (isSnakeCasePipelineStatus(raw)) return 'סטטוס טכני — ההודעה בטיפול במערכת';
  if (raw && String(raw).trim() && !hasHebrew(String(raw))) {
    return HEBREW_SNACKBAR.genericError;
  }
  return null;
}

/** טקסט ל־Tooltip ליד אייקון סטטוס — תמיד בעברית */
export function getOutgoingStatusTooltip(status: string | null | undefined): string {
  if (!status) return '';
  const mapped = mergedStatusLookup(status);
  if (mapped) return mapped;
  if (isSnakeCasePipelineStatus(status)) return 'סטטוס טכני — ההודעה בטיפול במערכת';
  if (hasHebrew(status)) return status.trim();
  return HEBREW_SNACKBAR.genericError;
}

/** טקסט לאזור השגיאה (אדום) — תמיד בעברית */
export function getErrorZoneDisplayText(raw: string | null | undefined): string | null {
  if (raw == null || raw === '') return null;
  const str = String(raw).trim();
  if (hasHebrew(str)) return str;
  const friendlyFromMeta = getFriendlyMessageForMetaRaw(str);
  if (friendlyFromMeta) return friendlyFromMeta;
  if (isSnakeCasePipelineStatus(str)) return 'סטטוס טכני — פנו לתמיכה אם הבעיה נמשכת';
  return HEBREW_SNACKBAR.genericError;
}

export type OutgoingMessageErrorResolution = {
  shouldShowErrorBlock: boolean;
  displayLine: string;
  /** מילה/ביטוי להדגשה בשורת התצוגה (למשל «בפייסbוק» בשגיאת תשלום) */
  displayLineEmphasisPhrase?: string | null;
  preventionText: string;
  whatsappErrorCode: string | null;
  /** מספר שגיאה ממטא לדיאלוג */
  metaErrorCode: string | null;
  /** נוסח השגיאה באנגלית כפי שהגיע ממטא */
  metaRawMessage: string | null;
  /** תוכן מותאם לתבנית שנחסמה בסינון ספאם */
  dialogVariant: OutgoingErrorDialogVariant;
  showMetaPolicyLink: boolean;
  /** כפתור/קישור לבדיקת איכות תבניות (ecosystem / ספאם / מדיניות) */
  showTemplateQualityAction: boolean;
  /** קישור לעדכון אמצעי תשלום ב-Business Manager */
  showMetaBillingLink: boolean;
  /** כפתור «שלח תבנית» (חלון 24 שעות) */
  showSendTemplateAction: boolean;
  /** כפתור «נסה שוב» (שגיאת שרת 500) */
  showRetrySendAction: boolean;
};

const TEMPLATE_QUALITY_ERROR_CODES = new Set(['131048', '131049']);

function isLikelyMetaRawMessage(raw: string | null | undefined): boolean {
  if (raw == null) return false;
  const s = String(raw).trim();
  if (!s || s.length < 4) return false;
  const lower = s.toLowerCase();
  if (KNOWN_INTERNAL_STATUS_VALUES.has(lower)) return false;
  if (/^\d+$/.test(s)) return false;
  if (hasHebrew(s)) return false;
  return true;
}

function extractMetaCodeFromRawMessage(raw: string): string | null {
  const hashMatch = raw.match(/\(#?(\d{3,6})\)/);
  if (hashMatch?.[1]) return hashMatch[1];
  const bare = raw.match(/\b(1\d{5})\b/);
  if (bare?.[1]) return bare[1];
  if (raw.trim() === '500' || raw.includes('Internal Server Error')) return '500';
  return null;
}

function stripMetaCodePrefix(raw: string): string {
  return raw.replace(/^\(#?\d+\)\s*/i, '').trim().toLowerCase();
}

/** חילוץ קוד Meta מ-(#131047), מספר голом, או התאמה לנוסח ה-API באנגלית */
function lookupMetaErrorCodeFromRawText(raw: string): string | null {
  const extracted = extractMetaCodeFromRawMessage(raw);
  if (extracted) {
    return normalizeCodeKey(extracted) ?? extracted;
  }

  const norm = stripMetaCodePrefix(raw);
  if (!norm) return null;

  for (const [code, metaRaw] of Object.entries(WHATSAPP_META_RAW_ENGLISH)) {
    const metaNorm = stripMetaCodePrefix(metaRaw);
    if (!metaNorm) continue;
    if (norm === metaNorm || norm.includes(metaNorm) || metaNorm.includes(norm)) {
      return code;
    }
  }

  for (const [code, aliases] of Object.entries(WHATSAPP_META_RAW_ENGLISH_ALIASES)) {
    for (const alias of aliases) {
      if (norm === alias || norm.includes(alias)) return code;
    }
  }

  return null;
}

/** מיפוי ידידותי ממחרוזת גולמית (Status / StatusHeb / webhook) — כולל חילוץ (#131047) */
function getFriendlyMessageForMetaRaw(raw: string | null | undefined): string | null {
  if (raw == null || !String(raw).trim()) return null;
  const str = String(raw).trim();
  if (isMetaGraphConnectionError(str)) return META_GRAPH_CONNECTION_ERROR_DISPLAY;
  const mapped = mergedStatusLookup(str);
  if (mapped) return mapped;
  const code = lookupMetaErrorCodeFromRawText(str);
  if (code && WHATSAPP_FRIENDLY_MESSAGES_CORE[code]) {
    return WHATSAPP_FRIENDLY_MESSAGES_CORE[code];
  }
  return resolveEnglishPhrase(str);
}

function resolveMetaRawErrorFieldsInternal(
  params: {
    status?: string | null;
    statusHeb?: string | null;
    whatsappErrorCode?: string | number | null;
  },
  resolvedCode: string | null,
): { metaErrorCode: string | null; metaRawMessage: string | null } {
  const status = params.status?.trim() ?? '';
  const statusHeb = params.statusHeb?.trim() ?? '';
  let code = resolvedCode;

  let metaRawMessage: string | null = null;
  if (isLikelyMetaRawMessage(status)) {
    metaRawMessage = status;
  } else if (isLikelyMetaRawMessage(statusHeb)) {
    metaRawMessage = statusHeb;
  }

  if (!code && metaRawMessage) {
    code = lookupMetaErrorCodeFromRawText(metaRawMessage) ?? extractMetaCodeFromRawMessage(metaRawMessage);
  }
  if (!code && /^\d+$/.test(statusHeb)) code = statusHeb;
  if (!code && /^\d+$/.test(status)) code = status;

  if (!metaRawMessage && code && WHATSAPP_META_RAW_ENGLISH[code]) {
    metaRawMessage = WHATSAPP_META_RAW_ENGLISH[code];
  }

  return { metaErrorCode: code, metaRawMessage };
}

function isTemplateSpamBlockError(
  fileType: string | null | undefined,
  isMeta: boolean,
  code: string | null,
  status: string,
): boolean {
  if (fileType?.toLowerCase() !== 'template') return false;
  return (
    isMeta ||
    isMetaHealthyEcosystemBlock(status) ||
    (code != null && TEMPLATE_QUALITY_ERROR_CODES.has(code))
  );
}

/** מספר שגיאה + נוסח גולמי ממטא לדיאלוג «מניעה וטיפול» */
export function resolveMetaRawErrorFields(params: {
  status?: string | null;
  statusHeb?: string | null;
  whatsappErrorCode?: string | number | null;
}): { metaErrorCode: string | null; metaRawMessage: string | null } {
  const code = resolveMessageWhatsappErrorCode({
    Status: params.status,
    StatusHeb: params.statusHeb,
    whatsappErrorCode: params.whatsappErrorCode,
  });
  return resolveMetaRawErrorFieldsInternal(params, code);
}

function isUnhelpfulStoredStatusHeb(raw: string): boolean {
  const trimmed = raw.trim();
  return (
    trimmed === HEBREW_SNACKBAR.genericError ||
    trimmed === OUTGOING_MESSAGE_STATUS_HEB.error ||
    trimmed === OUTGOING_ERROR_FALLBACK_REASON
  );
}

function isPaymentRelatedError(
  code: string | null,
  status: string,
  statusHeb: string,
): boolean {
  if (code === '131042') return true;
  const combined = `${status} ${statusHeb}`.toLowerCase();
  return (
    combined.includes('payment') ||
    combined.includes('billing') ||
    combined.includes('business eligibility') ||
    combined.includes('insufficient account balance')
  );
}

/**
 * מקור מידע יחיד לתצוגת שגיאה בהודעה יוצאת (שורה אדומה + דיאלוג הנחיה).
 * לא נוגע בלוגי workflow (InfoMessage).
 */
export function resolveOutgoingMessageError(params: {
  status?: string | null;
  statusHeb?: string | null;
  whatsappErrorCode?: string | number | null;
  fileType?: string | null;
}): OutgoingMessageErrorResolution {
  const status = params.status?.trim() ?? '';
  const statusLower = status.toLowerCase();
  const code = resolveMessageWhatsappErrorCode({
    Status: params.status,
    StatusHeb: params.statusHeb,
    whatsappErrorCode: params.whatsappErrorCode,
  });
  const { metaErrorCode, metaRawMessage } = resolveMetaRawErrorFieldsInternal(params, code);
  const effectiveCode = code ?? metaErrorCode;

  const isGraphConnectionError =
    isMetaGraphConnectionError(status) ||
    isMetaGraphConnectionError(params.statusHeb) ||
    isMetaGraphConnectionError(metaRawMessage);

  const userFacingMetaRawMessage =
    metaRawMessage && isMetaGraphConnectionError(metaRawMessage) ? null : metaRawMessage;

  const empty: OutgoingMessageErrorResolution = {
    shouldShowErrorBlock: false,
    displayLine: '',
    displayLineEmphasisPhrase: null,
    preventionText: GENERIC_OUTGOING_ERROR_PREVENTION,
    whatsappErrorCode: effectiveCode,
    metaErrorCode,
    metaRawMessage: userFacingMetaRawMessage,
    dialogVariant: 'default',
    showMetaPolicyLink: false,
    showTemplateQualityAction: false,
    showMetaBillingLink: false,
    showSendTemplateAction: false,
    showRetrySendAction: false,
  };

  if (!status) return empty;
  if (statusLower === 'send_failed') return empty;

  const isMeta = isMetaHealthyEcosystemBlock(status);
  const isStructuredError = statusLower === 'error';
  const isLegacyError = isOutgoingDeliveryErrorStatus(status);

  if (!isStructuredError && !isLegacyError && !isMeta) return empty;

  let displayLine = '';
  let preventionText: string | null = null;
  let showMetaPolicyLink = false;

  if (isGraphConnectionError) {
    displayLine = META_GRAPH_CONNECTION_ERROR_DISPLAY;
    preventionText = META_GRAPH_CONNECTION_ERROR_PREVENTION;
  }

  if (!displayLine && effectiveCode) {
    displayLine = getFriendlyErrorMessage({ errorCode: effectiveCode });
    preventionText = getErrorPreventionText(effectiveCode);
  }

  if (!displayLine && isMeta) {
    displayLine = META_ECOSYSTEM_DISPLAY_LINE;
    preventionText = META_ECOSYSTEM_PREVENTION;
    showMetaPolicyLink = true;
  }

  const shRaw = params.statusHeb?.trim() ?? '';
  const sh = isUnhelpfulStoredStatusHeb(shRaw) ? '' : shRaw;
  if (!displayLine && sh) {
    displayLine = getErrorZoneDisplayText(sh) ?? sh;
    preventionText = preventionText ?? getErrorPreventionText(sh);
  }

  if (!displayLine && status) {
    displayLine = getErrorZoneDisplayText(status) ?? HEBREW_SNACKBAR.genericError;
    preventionText = preventionText ?? getErrorPreventionText(status);
  }

  if (!displayLine && metaRawMessage) {
    //* '' ולא null: displayLine מוגדר כ-string (ריק = "אין שורת תצוגה"), וההשמה של null
    //* שברה את הבילד. הבדיקות בהמשך הן falsy ולכן ההתנהגות זהה.
    displayLine =
      getFriendlyMessageForMetaRaw(metaRawMessage) ??
      getErrorZoneDisplayText(metaRawMessage) ??
      '';
    preventionText = preventionText ?? getErrorPreventionText(metaRawMessage);
  }

  if (!preventionText && effectiveCode) {
    preventionText = getErrorPreventionText(effectiveCode);
  }

  if (isMeta) showMetaPolicyLink = true;

  const showTemplateQualityAction =
    isMeta || (effectiveCode != null && TEMPLATE_QUALITY_ERROR_CODES.has(effectiveCode));
  const showMetaBillingLink = isPaymentRelatedError(effectiveCode, status, sh);
  const showSendTemplateAction = effectiveCode === '131047';
  const showRetrySendAction = effectiveCode === '500' || isGraphConnectionError;
  const displayLineEmphasisPhrase = showMetaBillingLink ? META_PAYMENT_DISPLAY_EMPHASIS : null;
  const dialogVariant: OutgoingErrorDialogVariant = isTemplateSpamBlockError(
    params.fileType,
    isMeta,
    effectiveCode,
    status,
  )
    ? 'template_spam_block'
    : 'default';

  if (displayLine === HEBREW_SNACKBAR.genericError) {
    displayLine = OUTGOING_ERROR_FALLBACK_REASON;
  }

  return {
    shouldShowErrorBlock: true,
    displayLine,
    displayLineEmphasisPhrase,
    preventionText: preventionText ?? GENERIC_OUTGOING_ERROR_PREVENTION,
    whatsappErrorCode: effectiveCode,
    metaErrorCode,
    metaRawMessage: userFacingMetaRawMessage,
    dialogVariant,
    showMetaPolicyLink,
    showTemplateQualityAction,
    showMetaBillingLink,
    showSendTemplateAction,
    showRetrySendAction,
  };
}

/** מיפוי ידידותי (מומלץ); שם ישן לתאימות */
export const WHATSAPP_FRIENDLY_MESSAGES = WHATSAPP_FRIENDLY_MESSAGES_CORE;
export const WHATSAPP_META_ERROR_MAP = WHATSAPP_FRIENDLY_MESSAGES_CORE;

/** מניעה וטיפול — טקסט מפורט לדיאלוג "למידע נוסף" */
export const WHATSAPP_ERROR_PREVENTION: Record<string, string> = {
  '130429':
    'מטא מגבילה קצב שליחה כדי למנוע ספאם. המתינו מספר דקות, הפחיתו נפח הודעות, ואם השגיאה חוזרת — פתחו פנייה לתמיכה עם חותמת זמן.',
  '131026': MESSAGE_UNDELIVERABLE_PREVENTION,
  '131047': MESSAGE_RE_ENGAGEMENT_PREVENTION,
  '131049': META_ECOSYSTEM_PREVENTION,
  '131051': MESSAGE_UNDELIVERABLE_PREVENTION,
  '500':
    'השגיאה נרשמה אצלנו. נסו שוב בעוד כמה רגעים. אם הבעיה נמשכת, צלמו מסך ושלחו לתמיכה עם שעת השליחה.',
  '131031':
    'ודאו שהתבנית פעילה ב-Business Manager, לא פג תוקף, ושהשפה תואמת את השיחה. ייתכן שתבנית הושהתה על ידי מטא.',
  '132000': 'בדקו שכל השדות הנדרשים נשלחים ושפורמט המדיה/הטקסט תואם לדרישות ה-API.',
  '132001': 'מומלץ לנסות שוב בעוד מספר דקות. אם חוזר — בדקו סטטוס מערכות מטא.',
  '133010':
    'ודאו שה-WABA מחובר, שהטוקן בתוקף, ושחשבון העסק לא חסום. ב-Business Manager בדקו אימות, חיוב וסטטוס חשבון.',
  '131042':
    'שימו לב: מדובר באמצעי תשלום בחשבון פייסבוק (מטא) — לא בתשלום לפאנר.\n\nב-Business Manager → Billing: הוסיפו או החליפו כרטיס אשראי פעיל (ללא חסימה/פקיעת תוקף), ודאו פרטי חיוב ומס תקינים. לאחר העדכון המתינו מספר דקות ונסו שוב.',
  '131048':
    'לשגיאה הזו שתי סיבות אפשריות, ושתיהן נבדקות ב-WhatsApp Manager.\n\n'
    + '1. מכסת ההודעות היומית: לכל מספר יש תקרה של נמענים ייחודיים ב-24 שעות (מספר חדש שאינו מאומת מתחיל ב-250). '
    + 'כשהתקרה נגמרת, כל שליחה נוספת נכשלת עד שהחלון מתגלגל. בדקו את Messaging limit במסך המספרים, ופצלו תפוצה גדולה לכמה ימים.\n\n'
    + '2. דירוג איכות: שליחה לרשימה קרה, תלונות או חסימות מורידות את דירוג המספר ומטא מגבילה אותו. '
    + 'שלחו רק לנמענים שהסכימו לקבל, הימנעו מרשימות מיובאות שמעולם לא פנו אליכם, וכבדו בקשות הסרה.',
  '131056':
    'המתינו לפני שליחה חוזרת לאותו מספר, פזרו קמפיינים, ואל תשלחו פרצי הודעות לאותו לקוח.',
  '130472':
    'מדובר בניסוי מטא — שליחה חוזרת של אותה הודעה לרוב תחזיר שוב את אותה שגיאה. נסו ערוץ אחר או המתינו ללקוח שייזום שיחה.',
  '131052':
    'בקשו מהלקוח לשלוח את הקובץ מחדש או בערוץ אחר; בדקו ב-webhook את error_data.details אם קיים.',
  '131053':
    'בדקו MIME, גודל ופורמט לפי רשימת הסוגים הנתמכים; הורידו את הקובץ והעלו שוב לאחר תיקון.',
  '133004':
    'בדקו את דף סטטוס WhatsApp Business API (metastatus) ונסו שוב לאחר זמן קצר; אם חוזר — פנו לתמיכה עם חותמת זמן.',
  '131016':
    'בדקו סטטוס שירותי מטא, המתינו והפחיתו עומס API, ונסו שוב.',
  '131057':
    'המתינו לסיום תחזוקה/שדרוג; אם נמשך — בדקו במנהל העסק והתראות מטא.',
  '131000':
    'נסו שוב; אם חוזר — פתחו פנייה ל-Direct Support במטא עם מזהה בקשה וחותמת זמן.',
  '131005':
    'הריצו Access Token Debugger, ודאו הרשאות whatsapp_business_messaging וכו׳, ורעננו טוקן לפי הצורך.',
  '131037':
    'הגישו לאישור שם תצוגה במסך המספרים ב-Business Manager והמתינו לאישור מטא.',
  '131050':
    'אל תנסו שליחה חוזרת לשיווק לאותו מספר; הירשמו ל-webhook user_preferences כדי לעקוב אחרי הסכמה.',
  '132015':
    'ערכו את התבנית לפי המלצות איכות במטא והמתינו להסרת השהיה.',
  '132016':
    'צרו תבנית חדשה עם תוכן שונה; תבנית זו אינה זמינה לשימוש.',
  '132007':
    'התאימו את ניסוח התבנית למדיניות התבניות של מטא והגישו מחדש לאישור.',
};

/** מילוי אוטומטי של ביטויי Meta באנגלית → עברית + מניעה (לפי WHATSAPP_META_RAW_ENGLISH) */
function registerMetaRawEnglishPhraseMaps(): void {
  for (const [code, rawEn] of Object.entries(WHATSAPP_META_RAW_ENGLISH)) {
    const friendly = WHATSAPP_FRIENDLY_MESSAGES_CORE[code];
    const prevention = WHATSAPP_ERROR_PREVENTION[code];
    if (!friendly) continue;

    const variants = new Set<string>([rawEn.toLowerCase().trim(), stripMetaCodePrefix(rawEn)]);
    for (const key of variants) {
      if (!key) continue;
      if (!ENGLISH_STATUS_ERROR_PHRASES[key]) {
        ENGLISH_STATUS_ERROR_PHRASES[key] = friendly;
      }
      if (prevention && !ENGLISH_STATUS_ERROR_PREVENTION[key]) {
        ENGLISH_STATUS_ERROR_PREVENTION[key] = prevention;
      }
    }
  }
  refreshEnglishPhraseKeyIndex();
}

registerMetaRawEnglishPhraseMaps();

const WHATSAPP_INFO_CENTER_CODE_PRIORITY: readonly string[] = [
  '131042',
  '131047',
  '131049',
  '130429',
  '131026',
  '131048',
  '133004',
  '131016',
  '131057',
  '131000',
  '500',
  '131050',
  '131052',
  '131053',
  '131056',
  '130472',
  '131005',
  '131037',
  '131031',
  '132000',
  '132001',
  '132015',
  '132016',
  '132007',
  '133010',
  '131051',
];

export function getWhatsappMetaErrorCodesForInfoCenter(): string[] {
  const all = Object.keys(WHATSAPP_FRIENDLY_MESSAGES_CORE).filter((k) => {
    const t = k.trim();
    return /^\d+$/.test(t);
  });
  const prioritySet = new Set(WHATSAPP_INFO_CENTER_CODE_PRIORITY);
  const prioritized = WHATSAPP_INFO_CENTER_CODE_PRIORITY.filter((c) => all.includes(c));
  const rest = all.filter((c) => !prioritySet.has(c)).sort((a, b) => Number(a) - Number(b));
  return [...prioritized, ...rest];
}

export type WhatsappMetaErrorInfoRow = {
  code: string;
  chatShortMessage: string;
  preventionForAgents: string | null;
};

export function getWhatsappMetaErrorInfoRows(): WhatsappMetaErrorInfoRow[] {
  return getWhatsappMetaErrorCodesForInfoCenter().map((code) => ({
    code,
    chatShortMessage: WHATSAPP_FRIENDLY_MESSAGES_CORE[code] ?? '',
    preventionForAgents: WHATSAPP_ERROR_PREVENTION[code] ?? null,
  }));
}

export const WHATSAPP_INFO_CENTER_API_CONTEXT_LINE =
  'השגיאה נרשמת לפי WhatsApp Cloud API (מטא) כאשר השליחה נכשלת; הקוד מזהה את סוג הבעיה.';

export const HEBREW_SNACKBAR = {
  genericError: 'חלה שגיאה בעיבוד הנתונים',
  genericSuccess: 'הפעולה בוצעה בהצלחה',
  networkError: 'נראה שיש בעיית חיבור לאינטרנט, אנא בדקו את הרשת',
} as const;

export type FriendlyApiErrorInput =
  | string
  | number
  | null
  | undefined
  | {
      errorCode?: string | number | null;
      httpStatus?: number | null;
      message?: string | null;
    };

function normalizeCodeKey(code: string | number | null | undefined): string | null {
  if (code == null) return null;
  const s = String(code).trim();
  if (!s) return null;
  if (s === '500' || s === '503' || s === '502') return '500';
  return s;
}

export function getFriendlyErrorMessage(apiError: FriendlyApiErrorInput): string {
  if (apiError == null) {
    return HEBREW_SNACKBAR.genericError;
  }

  if (typeof apiError === 'string' || typeof apiError === 'number') {
    const str = String(apiError).trim();
    const friendlyFromMeta = getFriendlyMessageForMetaRaw(str);
    if (friendlyFromMeta) return friendlyFromMeta;
    if (isSnakeCasePipelineStatus(str)) {
      return 'סטטוס טכני — פנו לתמיכה אם הבעיה נמשכת';
    }
    return HEBREW_SNACKBAR.genericError;
  }

  const http = apiError.httpStatus;
  if (http === 500 || http === 502 || http === 503) {
    return WHATSAPP_FRIENDLY_MESSAGES['500'];
  }

  const key = normalizeCodeKey(apiError.errorCode ?? (http != null && http >= 500 ? 500 : undefined));
  if (key && WHATSAPP_FRIENDLY_MESSAGES[key]) {
    return WHATSAPP_FRIENDLY_MESSAGES[key];
  }

  if (apiError.message && typeof apiError.message === 'string' && apiError.message.trim()) {
    const msg = apiError.message.trim();
    const translated = resolveEnglishPhrase(msg) ?? mergedStatusLookup(msg);
    if (translated) return translated;
    if (hasHebrew(msg)) return msg;
    return HEBREW_SNACKBAR.genericError;
  }

  return HEBREW_SNACKBAR.genericError;
}

/** טקסט "מניעה וטיפול" — לפי קוד Meta או מחרוזת שגיאה */
export function getErrorPreventionText(
  codeOrStatus: string | number | null | undefined,
): string | null {
  if (codeOrStatus == null) return null;
  const str = String(codeOrStatus).trim();
  if (!str) return null;

  if (isMessageUndeliverableError(str)) return MESSAGE_UNDELIVERABLE_PREVENTION;
  if (isReEngagementError(str)) return MESSAGE_RE_ENGAGEMENT_PREVENTION;
  if (isMetaGraphConnectionError(str)) return META_GRAPH_CONNECTION_ERROR_PREVENTION;

  const viaRawLookup = lookupMetaErrorCodeFromRawText(str);
  if (viaRawLookup && WHATSAPP_ERROR_PREVENTION[viaRawLookup]) {
    return WHATSAPP_ERROR_PREVENTION[viaRawLookup];
  }

  const key = normalizeCodeKey(str);
  if (key && WHATSAPP_ERROR_PREVENTION[key]) {
    return WHATSAPP_ERROR_PREVENTION[key];
  }

  const phrasePrev = resolveEnglishPhrasePrevention(str);
  if (phrasePrev) return phrasePrev;

  if (isMetaHealthyEcosystemBlock(str)) return META_ECOSYSTEM_PREVENTION;

  return null;
}

export function getWhatsappMetaErrorMessage(code: string | number | undefined | null): string | null {
  if (code == null) return null;
  const key = normalizeCodeKey(code);
  if (!key) return null;
  return WHATSAPP_FRIENDLY_MESSAGES[key] ?? null;
}

export function resolveMessageWhatsappErrorCode(message: {
  Status?: string | null;
  StatusHeb?: string | null;
  whatsappErrorCode?: string | number | null;
}): string | null {
  if (message.whatsappErrorCode != null && String(message.whatsappErrorCode).trim() !== '') {
    return normalizeCodeKey(message.whatsappErrorCode) ?? String(message.whatsappErrorCode).trim();
  }

  for (const raw of [message.StatusHeb, message.Status]) {
    if (raw == null) continue;
    const trimmed = String(raw).trim();
    if (!trimmed) continue;
    if (/^\d+$/.test(trimmed)) {
      return normalizeCodeKey(trimmed) ?? trimmed;
    }
    const viaLookup = lookupMetaErrorCodeFromRawText(trimmed);
    if (viaLookup) {
      return viaLookup;
    }
  }

  return null;
}

export type OutgoingParseResult = {
  errorCode?: string | number;
  message?: string;
  httpStatus?: number;
};

export function parseOutgoingSendError(err: unknown): OutgoingParseResult {
  if (err == null || typeof err !== 'object') return {};

  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const data = err.response?.data;
    if (typeof data === 'object' && data !== null) {
      const o = data as Record<string, unknown>;
      const code = o.errorCode ?? o.code ?? o.metaErrorCode ?? o.ErrorCode ?? o.meta_error_code;
      const errorCode =
        typeof code === 'number' || typeof code === 'string' ? code : status === 500 ? 500 : undefined;
      const message =
        typeof o.message === 'string'
          ? o.message
          : typeof o.Message === 'string'
            ? o.Message
            : typeof o.error === 'string'
              ? o.error
              : undefined;
      return {
        errorCode,
        message,
        httpStatus: status,
      };
    }
    if (status === 500) {
      return { errorCode: 500, httpStatus: 500 };
    }
    return { httpStatus: status };
  }

  const o = err as Record<string, unknown>;
  const code = o.errorCode ?? o.code ?? o.metaErrorCode ?? o.ErrorCode ?? o.meta_error_code;
  const errorCode =
    typeof code === 'number' || typeof code === 'string' ? code : undefined;
  const message =
    typeof o.message === 'string'
      ? o.message
      : typeof o.Message === 'string'
        ? o.Message
        : typeof o.error === 'string'
          ? o.error
          : undefined;
  return { errorCode, message };
}
